import moment from 'moment';
import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps } from 'src/Library/Types';
import { QuoteExchangeRateType, QuoteType } from '../../../SE.interfaces';
import { SEQuoteExchangeRatesStyles } from './SEQuoteExchangeRates.styles';

const styles = () => ({ ...SEQuoteExchangeRatesStyles() });

/** Same heading + date format as the Additional Charges exchange-rate footer, so the two read alike. */
export const QUOTE_EXCHANGE_RATES_HEADING = 'Exchange Rates';
const QUOTE_EXCHANGE_RATE_DATE_DISPLAY_FORMAT = 'DD-MM-YYYY';

/**
 * The read-only / approved view never calls quote-search, so it rebuilds the footer from the frozen Quote
 * Candidates snapshot: each captured card carries the tab-level rates it was ranked with. Deduped by
 * currency (every card of a tab carries the same list), sorted for a stable order. Snapshots captured
 * before the backend stamped rates simply yield `[]` — no footer.
 */
export const collectQuoteExchangeRates = (quotes: QuoteType[]): QuoteExchangeRateType[] => {
  const byCurrency: Record<string, QuoteExchangeRateType> = {};
  quotes.forEach((quote) => {
    (quote?.exchangeRates || []).forEach((entry) => {
      if (entry?.currency && !byCurrency[entry.currency]) byCurrency[entry.currency] = entry;
    });
  });
  return Object.values(byCurrency).sort((a, b) => a.currency.localeCompare(b.currency));
};

interface SEQuoteExchangeRatesProps extends StylesProps<ReturnType<typeof styles>> {
  exchangeRates: QuoteExchangeRateType[];
}

/**
 * "Exchange Rates" footer under the GPO cards: one line per non-INR currency the ranking converted, e.g.
 * "1 USD = 95.434533 INR (as of 29-08-2026)". The date is the Quote Selection task date the rates are frozen
 * to. Renders nothing for an INR-only tab.
 */
const SEQuoteExchangeRates = (props: SEQuoteExchangeRatesProps): React.ReactElement | null => {
  const { classes, exchangeRates } = props;
  if (!exchangeRates.length) return null;
  return (
    <div className={classes.exchangeRatesFooter}>
      <strong>{QUOTE_EXCHANGE_RATES_HEADING}</strong>
      {exchangeRates.map((entry) => (
        <div key={`${entry.currency}|${entry.rate}|${entry.asOf}`}>
          {`1 ${entry.currency} = ${entry.rate} INR`}
          {entry.asOf ? ` (as of ${moment(entry.asOf).format(QUOTE_EXCHANGE_RATE_DATE_DISPLAY_FORMAT)})` : ''}
        </div>
      ))}
    </div>
  );
};

export default withStyles(styles)(SEQuoteExchangeRates);
